import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Delete, LogIn, LogOut, Users as UsersIcon, Pencil, Trash2, Plus, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const fmtHours = (secs) => ((secs || 0) / 3600).toFixed(2) + "h";
const fmtClock = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch { return "—"; }
};

const PIN_LENGTH = 4;

/**
 * Kiosk Punch Clock — big PIN pad for crew to clock in / out without a login.
 * - Keyboard: 0-9 (incl. NumLock), Backspace, Enter (auto-submits on 4 digits).
 * - On-screen 3x4 keypad for touch devices.
 * - Shows a large confirmation banner on success for 4 seconds.
 */
export default function KioskPunchClock({ onPunch }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { action, crew, duration_seconds, at }
  const [error, setError] = useState("");
  const [showCrew, setShowCrew] = useState(false);
  const resultTimerRef = useRef(null);

  const clearResultSoon = useCallback(() => {
    if (resultTimerRef.current) clearTimeout(resultTimerRef.current);
    resultTimerRef.current = setTimeout(() => setResult(null), 4000);
  }, []);

  const submit = useCallback(async (value) => {
    if (busy) return;
    if (value.length !== PIN_LENGTH) {
      setError("Enter a 4-digit PIN");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/time/kiosk-punch", { pin: value });
      setResult(data);
      setPin("");
      clearResultSoon();
      if (onPunch) onPunch(data);
    } catch (e) {
      const msg = e.response?.data?.detail || "Punch failed";
      setError(msg);
      // short shake, then clear PIN so they can retry
      setTimeout(() => setPin(""), 400);
    } finally {
      setBusy(false);
    }
  }, [busy, onPunch, clearResultSoon]);

  const appendDigit = useCallback((d) => {
    setError("");
    setPin((prev) => {
      if (prev.length >= PIN_LENGTH) return prev;
      const next = prev + d;
      if (next.length === PIN_LENGTH) {
        // auto-submit when they hit 4 digits
        setTimeout(() => submit(next), 50);
      }
      return next;
    });
  }, [submit]);

  const backspace = useCallback(() => {
    setError("");
    setPin((prev) => prev.slice(0, -1));
  }, []);

  const clearAll = useCallback(() => {
    setError("");
    setPin("");
  }, []);

  // Global keyboard listener (numeric row + numpad + Backspace + Enter + Escape)
  useEffect(() => {
    const handler = (e) => {
      // ignore when user is typing inside an input/textarea (e.g. crew dialog)
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      if (showCrew) return;
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        appendDigit(e.key);
      } else if (e.key === "Backspace") {
        e.preventDefault();
        backspace();
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (pin.length === PIN_LENGTH) submit(pin);
      } else if (e.key === "Escape") {
        clearAll();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [appendDigit, backspace, submit, clearAll, pin, showCrew]);

  useEffect(() => () => {
    if (resultTimerRef.current) clearTimeout(resultTimerRef.current);
  }, []);

  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 no-print" data-testid="kiosk-panel">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Crew punch clock</div>
          <div className="mt-0.5 font-display text-2xl font-black tracking-tighter text-slate-900">
            Enter your 4-digit PIN
          </div>
          <div className="text-xs text-slate-500 mt-1 font-mono">
            Use the number pad or tap below · same PIN clocks in, clocks out
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowCrew(true)}
          data-testid="manage-crew-btn"
          className="rounded-sm border-slate-300"
        >
          <UsersIcon size={16} className="mr-1.5" /> Manage Crew
        </Button>
      </div>

      {/* PIN display */}
      <div className="flex items-center justify-center gap-3 py-6" data-testid="pin-display">
        {Array.from({ length: PIN_LENGTH }).map((_, i) => {
          const filled = i < pin.length;
          return (
            <div
              key={i}
              data-testid={`pin-dot-${i}`}
              className={`h-16 w-14 rounded-sm border-2 grid place-items-center font-mono text-3xl font-black transition-all ${
                filled ? "border-blue-800 bg-blue-50 text-blue-900 scale-105" : "border-slate-300 bg-slate-50 text-slate-300"
              } ${error ? "border-red-500 bg-red-50" : ""}`}
            >
              {filled ? "●" : ""}
            </div>
          );
        })}
      </div>

      {/* Error / result banner */}
      {error && (
        <div
          data-testid="kiosk-error"
          className="mb-4 rounded-sm border border-red-200 bg-red-50 text-red-800 text-sm px-4 py-2 text-center font-semibold"
        >
          {error}
        </div>
      )}
      {result && !error && (
        <div
          data-testid="kiosk-success"
          className={`mb-4 rounded-sm border px-4 py-4 text-center ${
            result.action === "clock_in"
              ? "border-emerald-300 bg-emerald-50 text-emerald-900"
              : "border-blue-300 bg-blue-50 text-blue-900"
          }`}
        >
          <div className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest">
            {result.action === "clock_in" ? <LogIn size={14} /> : <LogOut size={14} />}
            {result.action === "clock_in" ? "Clocked in" : "Clocked out"}
          </div>
          <div className="mt-1 font-display text-3xl font-black tracking-tight">
            {result.crew?.name}
          </div>
          <div className="mt-1 text-sm font-mono">
            {fmtClock(result.at)}
            {result.action === "clock_out" && result.duration_seconds != null && (
              <> · {fmtHours(result.duration_seconds)} this shift</>
            )}
          </div>
        </div>
      )}

      {/* On-screen keypad */}
      <div className="grid grid-cols-3 gap-2 max-w-sm mx-auto" data-testid="kiosk-keypad">
        {["1","2","3","4","5","6","7","8","9"].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => appendDigit(n)}
            disabled={busy}
            data-testid={`pin-key-${n}`}
            className="h-16 rounded-sm bg-slate-100 hover:bg-slate-200 border border-slate-200 font-mono text-2xl font-black text-slate-900 active:scale-95 transition-transform disabled:opacity-50"
          >
            {n}
          </button>
        ))}
        <button
          type="button"
          onClick={clearAll}
          disabled={busy}
          data-testid="pin-key-clear"
          className="h-16 rounded-sm bg-slate-100 hover:bg-slate-200 border border-slate-200 text-xs font-bold uppercase tracking-widest text-slate-700 active:scale-95 transition-transform"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={() => appendDigit("0")}
          disabled={busy}
          data-testid="pin-key-0"
          className="h-16 rounded-sm bg-slate-100 hover:bg-slate-200 border border-slate-200 font-mono text-2xl font-black text-slate-900 active:scale-95 transition-transform disabled:opacity-50"
        >
          0
        </button>
        <button
          type="button"
          onClick={backspace}
          disabled={busy}
          data-testid="pin-key-backspace"
          className="h-16 rounded-sm bg-slate-100 hover:bg-slate-200 border border-slate-200 grid place-items-center text-slate-700 active:scale-95 transition-transform"
        >
          <Delete size={22} />
        </button>
      </div>

      <CrewManagerDialog open={showCrew} onOpenChange={setShowCrew} />
    </div>
  );
}


function CrewManagerDialog({ open, onOpenChange }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPin, setNewPin] = useState("");
  const [editing, setEditing] = useState(null); // {id, name, pin}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/crew");
      setMembers(data);
    } catch (e) {
      toast.error("Could not load crew list");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const addMember = async () => {
    const name = newName.trim();
    const pin = newPin.trim();
    if (!name) return toast.error("Enter a name");
    if (!/^\d{4}$/.test(pin)) return toast.error("PIN must be 4 digits");
    try {
      await api.post("/crew", { name, pin });
      setNewName(""); setNewPin("");
      await load();
      toast.success(`${name} added`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add crew member");
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const name = (editing.name || "").trim();
    const pin = (editing.pin || "").trim();
    if (!name) return toast.error("Enter a name");
    const body = { name };
    if (pin) {
      if (!/^\d{4}$/.test(pin)) return toast.error("PIN must be 4 digits");
      body.pin = pin;
    }
    try {
      await api.patch(`/crew/${editing.id}`, body);
      setEditing(null);
      await load();
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };

  const toggleActive = async (m) => {
    try {
      await api.patch(`/crew/${m.id}`, { active: !m.active });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name}? Their past time entries stay in the log.`)) return;
    try {
      await api.delete(`/crew/${m.id}`);
      await load();
      toast.success("Removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="crew-manager-dialog">
        <DialogHeader>
          <DialogTitle>Crew Members</DialogTitle>
          <DialogDescription>
            Add every teammate who punches the clock. Each needs a unique 4-digit PIN.
          </DialogDescription>
        </DialogHeader>

        {/* Add row */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_auto] gap-2 items-end">
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Name</Label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Mike Thompson"
              data-testid="crew-new-name"
              className="rounded-sm"
            />
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">4-digit PIN</Label>
            <Input
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="0000"
              inputMode="numeric"
              maxLength={4}
              data-testid="crew-new-pin"
              className="rounded-sm font-mono tracking-widest text-center"
            />
          </div>
          <Button onClick={addMember} data-testid="crew-add-btn" className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm h-10">
            <Plus size={16} className="mr-1.5" /> Add
          </Button>
        </div>

        {/* List */}
        <div className="mt-2 border border-slate-200 rounded-sm max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-sm text-slate-500 font-mono">Loading…</div>
          ) : members.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500 font-mono">No crew members yet.</div>
          ) : (
            <ul className="divide-y divide-slate-200">
              {members.map((m) => (
                <li key={m.id} className="px-4 py-2 flex items-center gap-3" data-testid={`crew-row-${m.id}`}>
                  {editing?.id === m.id ? (
                    <>
                      <Input
                        value={editing.name}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                        className="rounded-sm flex-1"
                        data-testid="crew-edit-name"
                      />
                      <Input
                        value={editing.pin || ""}
                        onChange={(e) => setEditing({ ...editing, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                        placeholder="Leave blank to keep"
                        inputMode="numeric"
                        maxLength={4}
                        className="rounded-sm w-40 font-mono text-center"
                        data-testid="crew-edit-pin"
                      />
                      <Button onClick={saveEdit} className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm h-9" data-testid="crew-edit-save">Save</Button>
                      <Button variant="ghost" onClick={() => setEditing(null)} className="h-9"><X size={16} /></Button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <div className={`font-semibold ${m.active ? "text-slate-900" : "text-slate-400 line-through"}`}>{m.name}</div>
                        <div className="text-xs text-slate-500 font-mono">PIN set · {m.active ? "active" : "inactive"}</div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => toggleActive(m)} data-testid={`crew-toggle-${m.id}`}>
                        {m.active ? "Deactivate" : "Activate"}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditing({ id: m.id, name: m.name, pin: "" })} data-testid={`crew-edit-${m.id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(m)} data-testid={`crew-remove-${m.id}`}>
                        <Trash2 size={14} className="text-red-600" />
                      </Button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
